<script setup>
/**
 * The queue as a kanban board, cards draggable between its columns.
 *
 * A column is either a stage of the selected ticket type or one of the system
 * stage categories when the queue spans every type — see BOARD_GROUP_BY in the
 * store. This component does not know which: it renders whatever columns it is
 * handed, each identified by its own `key` and described by a `stage` shape.
 *
 * The store is the source of truth for membership; the local copies exist only
 * because vuedraggable mutates the arrays it renders. A drop emits `move` and
 * nothing else — the screen owns the mutation, the store owns the optimistic
 * update and its rollback, and the props flowing back down resync the copies
 * either way. Columns paginate independently ("load more" per column), because
 * a column is its own list to the API.
 */
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';

import Button from 'dashboard/components-next/button/Button.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import CaseBoardCard from './CaseBoardCard.vue';
import { stageCategoryMeta } from 'dashboard/helper/supportCaseMeta';

const props = defineProps({
  // `[{ key, stage, cases, meta, isLoading }]` in board order.
  columns: {
    type: Array,
    default: () => [],
  },
  // Truthy when the board fetch failed; the message is this component's to own,
  // mirroring the list table.
  hasError: {
    type: Boolean,
    default: false,
  },
  // The archived queue is read-only on the server, so its board does not drag.
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['select', 'move', 'loadMore', 'retry']);

const SKELETON_CARDS = 3;

const { t } = useI18n();

// vuedraggable mutates `:list` in place, so each column drags a copy of its
// cases rather than the store's own array.
const localColumns = ref([]);

watch(
  () => props.columns,
  columns => {
    localColumns.value = columns.map(column => ({
      ...column,
      cases: [...column.cases],
    }));
  },
  { immediate: true, deep: true }
);

// The WIP limit is soft: the column reports its state and never blocks a drop.
// Amber at the limit, ruby over it — under it (or without a rule) the count
// stays the quiet slate figure it always was.
const wipCountClass = column => {
  const limit = column.stage.wipLimit;
  if (!limit || column.meta.count < limit) return 'text-n-slate-11';
  return column.meta.count > limit
    ? 'font-medium text-n-ruby-11'
    : 'font-medium text-n-amber-11';
};

const wipCountLabel = column => `${column.meta.count}/${column.stage.wipLimit}`;

// Only the receiving column reports `added` for a cross-column drop; the
// source column's `removed` is the same move seen from the other side, so one
// handler emits exactly one event per drop. A reorder within a column is
// `moved` and means nothing here — the server owns the sort.
//
// The whole column rides along because a category column names no stage on its
// own: resolving it against the card's ticket type is the screen's job.
const onChange = (column, event) => {
  if (!event.added) return;
  emit('move', { supportCase: event.added.element, column });
};
</script>

<template>
  <EmptyStateLayout
    v-if="hasError"
    compact
    icon="i-lucide-triangle-alert"
    :title="t('SUPPORT_CASES.LIST.ERROR.TITLE')"
    :subtitle="t('SUPPORT_CASES.LIST.ERROR.SUBTITLE')"
    data-testid="case-board-error"
  >
    <template #actions>
      <Button
        size="sm"
        :label="t('SUPPORT_CASES.LIST.ERROR.ACTION')"
        @click="emit('retry')"
      />
    </template>
  </EmptyStateLayout>

  <EmptyStateLayout
    v-else-if="!localColumns.length"
    compact
    icon="i-lucide-kanban"
    :title="t('SUPPORT_CASES.BOARD.NO_STAGES.TITLE')"
    :subtitle="t('SUPPORT_CASES.BOARD.NO_STAGES.SUBTITLE')"
    data-testid="case-board-no-stages"
  />

  <div
    v-else
    class="flex items-stretch w-full h-full min-h-0 gap-3 overflow-x-auto"
    data-testid="case-board"
  >
    <section
      v-for="column in localColumns"
      :key="column.key"
      class="flex flex-col w-72 shrink-0 min-h-0 rounded-xl bg-n-alpha-1"
      :aria-label="column.stage.name"
      :data-testid="`case-board-column-${column.key}`"
    >
      <header class="flex items-center gap-2 px-3 py-2.5">
        <span
          class="rounded-full size-2 shrink-0"
          :class="stageCategoryMeta(column.stage.category).dotClass"
          aria-hidden="true"
        />
        <h3 class="mb-0 text-sm font-medium truncate text-n-slate-12">
          {{ column.stage.name }}
        </h3>
        <span
          v-if="column.stage.wipLimit"
          class="text-xs tabular-nums"
          :class="wipCountClass(column)"
          :aria-label="
            t('SUPPORT_CASES.BOARD.WIP_STATUS', {
              count: column.meta.count,
              limit: column.stage.wipLimit,
            })
          "
          :data-testid="`case-board-wip-${column.key}`"
        >
          {{ wipCountLabel(column) }}
        </span>
        <span v-else class="text-xs tabular-nums text-n-slate-11">
          {{ column.meta.count }}
        </span>
      </header>

      <div
        v-if="column.isLoading && !column.cases.length"
        role="status"
        :aria-label="t('SUPPORT_CASES.LIST.LOADING')"
        aria-busy="true"
        class="flex flex-col gap-2 px-2 pb-2"
      >
        <Skeleton
          v-for="card in SKELETON_CARDS"
          :key="card"
          variant="text"
          :announce="false"
          class="h-20 rounded-lg"
        />
      </div>

      <Draggable
        v-else
        :list="column.cases"
        item-key="id"
        group="case-board"
        :disabled="readonly"
        ghost-class="opacity-50"
        class="flex flex-col min-h-16 gap-2 px-2 pb-2 overflow-y-auto grow"
        @change="onChange(column, $event)"
      >
        <template #item="{ element }">
          <CaseBoardCard
            :support-case="element"
            :class="{ 'cursor-grab': !readonly }"
            @select="emit('select', $event)"
          />
        </template>
        <template #footer>
          <p
            v-if="!column.cases.length"
            class="px-1 py-2 mb-0 text-xs text-center text-n-slate-10"
          >
            {{ t('SUPPORT_CASES.BOARD.EMPTY_COLUMN') }}
          </p>
          <Button
            v-if="column.meta.currentPage < column.meta.totalPages"
            variant="ghost"
            color="slate"
            size="sm"
            :is-loading="column.isLoading"
            :label="t('SUPPORT_CASES.BOARD.LOAD_MORE')"
            :data-testid="`case-board-load-more-${column.key}`"
            @click="emit('loadMore', column)"
          />
        </template>
      </Draggable>
    </section>
  </div>
</template>
